import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowRight, ShieldCheck, TrendingUp, Workflow, Target, FileText, Mail, Cpu, Lock, Sparkles, Linkedin, Copy } from "lucide-react";
import { toast } from "sonner";
import useSEO from "@/hooks/useSEO";

export default function Landing() {
  const nav = useNavigate();
  const { t } = useTranslation();

  useSEO({
    title: t("landing.seo_title"),
    description: t("landing.seo_description"),
    canonical: "https://iam-calculator.emergent.host/",
  });

  const shareUrl = typeof window !== "undefined" ? window.location.origin : "";
  const shareText = encodeURIComponent(t("landing.share.share_text"));

  const onCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    toast.success(t("landing.share.copied_toast"));
  };

  const valueIcons = [TrendingUp, ShieldCheck, Workflow];
  const valueProps = [1, 2, 3].map((n, i) => ({
    icon: valueIcons[i],
    title: t(`landing.value.card${n}_title`),
    text: t(`landing.value.card${n}_text`),
  }));

  const faqItems = t("landing.faq.items", { returnObjects: true });
  const methodItems = t("landing.method.items", { returnObjects: true });

  const chips = [
    { i: Target, t: t("landing.cta_final.chip1_t"), d: t("landing.cta_final.chip1_d"), c: "text-fuchsia-600" },
    { i: FileText, t: t("landing.cta_final.chip2_t"), d: t("landing.cta_final.chip2_d"), c: "text-orange-500" },
    { i: Mail, t: t("landing.cta_final.chip3_t"), d: t("landing.cta_final.chip3_d"), c: "text-slate-900" },
    { i: ShieldCheck, t: t("landing.cta_final.chip4_t"), d: t("landing.cta_final.chip4_d"), c: "text-fuchsia-600" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 relative overflow-hidden">
      <Header />

      {/* HERO */}
      <section className="relative">
        <div className="ar-blob bg-fuchsia-500" style={{ width: 460, height: 460, top: -100, right: -100, opacity: 0.35 }} />
        <div className="ar-blob bg-orange-400" style={{ width: 380, height: 380, top: 280, left: -120, opacity: 0.25 }} />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex flex-wrap items-center gap-3 mb-8" data-testid="hero-eyebrow">
                <div className="inline-flex items-center gap-2 text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold">
                  <span className="h-px w-8 bg-slate-300" />
                  {t("landing.hero.eyebrow")}
                </div>
                <span
                  data-testid="hero-badge-free"
                  className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.16em] uppercase font-bold text-white px-3 py-1 rounded-full ar-gradient-bg ar-glow"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-white" />
                  {t("landing.hero.badge_free")}
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-slate-900 leading-[1.02]" data-testid="hero-title">
                {t("landing.hero.title_1")} <span className="ar-gradient-text">{t("landing.hero.title_roi")}</span> {t("landing.hero.title_2")} <span className="ar-gradient-text">{t("landing.hero.title_iam")}</span>.
                <br />
                {t("landing.hero.title_3")}
              </h1>

              <p className="mt-7 text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="hero-subtitle">
                {t("landing.hero.subtitle")}
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-4">
                <button
                  onClick={() => nav("/calculer")}
                  data-testid="hero-cta-start"
                  className="group relative inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950 ar-glow"
                >
                  {t("landing.hero.cta_start")}
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </button>
                <a
                  href="#methode"
                  className="px-6 py-4 text-sm text-slate-700 hover:text-slate-900 font-medium tracking-tight transition"
                  data-testid="hero-cta-method"
                >
                  {t("landing.hero.cta_method")}
                </a>
              </div>

              <div className="mt-12 flex items-center gap-8 text-xs text-slate-500 flex-wrap">
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-fuchsia-600" />{t("landing.hero.trust_scenarios")}</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-slate-900" />{t("landing.hero.trust_pdf")}</div>
                <div className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-orange-500" />{t("landing.hero.trust_free")}</div>
              </div>
            </div>

            {/* Sidebar visuel */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-3xl bg-slate-950 p-8 text-white overflow-hidden ar-glow">
                <div className="ar-grid-circuits absolute inset-0 opacity-30" />
                <div className="relative">
                  <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 mb-6">{t("landing.hero.preview_label")}</div>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">{t("landing.hero.preview_roi")}</div>
                      <div className="text-4xl font-bold ar-gradient-text mt-1">+187 %</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">{t("landing.hero.preview_payback")}</div>
                      <div className="text-4xl font-bold text-white mt-1">18 <span className="text-base text-slate-400">{t("common.months")}</span></div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">{t("landing.hero.preview_van")}</div>
                      <div className="text-2xl font-semibold text-white mt-1">1,12 M€</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-wider text-slate-400 uppercase">{t("landing.hero.preview_annual")}</div>
                      <div className="text-2xl font-semibold text-white mt-1">420 K€</div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-slate-800">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                      <span>{t("landing.hero.preview_ops")}</span><span>48 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-slate-100" style={{ width: "48%" }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-4 mb-2">
                      <span>{t("landing.hero.preview_sam")}</span><span>22 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-fuchsia-500" style={{ width: "22%" }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-4 mb-2">
                      <span>{t("landing.hero.preview_cyber")}</span><span>30 %</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full bg-orange-500" style={{ width: "30%" }} />
                    </div>
                  </div>

                  <div className="mt-7 text-[10px] text-slate-500 leading-relaxed">
                    {t("landing.hero.preview_footer")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALEUR */}
      <section id="valeur" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="mb-14">
          <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">{t("landing.value.eyebrow")}</div>
          <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight max-w-3xl">
            {t("landing.value.title")}
          </h2>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {valueProps.map((vp, i) => (
            <div key={i} className="relative group bg-white rounded-2xl p-8 border border-slate-200 hover:border-slate-300 transition-all duration-300 hover:-translate-y-1" data-testid={`value-card-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-slate-900 flex items-center justify-center mb-6">
                <vp.icon className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3 tracking-tight">{vp.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{vp.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST */}
      <section id="trust" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">{t("landing.trust.eyebrow")}</div>
            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-[1.05]">
              {t("landing.trust.title_1")}<br />
              <span className="ar-gradient-text">{t("landing.trust.title_2")}</span>
            </h2>
            <p className="text-slate-600 mt-6 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("landing.trust.para_1") }} />
            <p className="text-slate-600 mt-4 leading-relaxed" dangerouslySetInnerHTML={{ __html: t("landing.trust.para_2") }} />
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            <TrustCard icon={Sparkles} eyebrow={t("landing.trust.card_ai_eyebrow")} title={t("landing.trust.card_ai_title")} text={t("landing.trust.card_ai_text")} testid="trust-card-ai" />
            <TrustCard icon={Lock} eyebrow={t("landing.trust.card_privacy_eyebrow")} title={t("landing.trust.card_privacy_title")} text={t("landing.trust.card_privacy_text")} testid="trust-card-privacy" />
            <TrustCard icon={Cpu} eyebrow={t("landing.trust.card_method_eyebrow")} title={t("landing.trust.card_method_title")} text={t("landing.trust.card_method_text")} testid="trust-card-method" />
            <TrustCard icon={ShieldCheck} eyebrow={t("landing.trust.card_free_eyebrow")} title={t("landing.trust.card_free_title")} text={t("landing.trust.card_free_text")} testid="trust-card-free" />
          </div>
        </div>

        {/* SHARE */}
        <div className="mt-16 relative rounded-3xl overflow-hidden bg-slate-50 border border-slate-200 p-8 lg:p-10">
          <div className="grid lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-3">{t("landing.share.eyebrow")}</div>
              <h3 className="text-2xl lg:text-3xl font-bold text-slate-900 tracking-tight leading-tight">
                {t("landing.share.title_1")}<br />
                <span className="ar-gradient-text">{t("landing.share.title_2")}</span>
              </h3>
              <p className="text-slate-600 mt-4 max-w-xl text-sm leading-relaxed">
                {t("landing.share.para")}
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-col gap-3">
              <a
                href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="share-linkedin"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950"
              >
                <Linkedin className="w-4 h-4" />
                {t("landing.share.linkedin")}
              </a>
              <a
                href={`mailto:?subject=${encodeURIComponent(t("landing.share.share_subject"))}&body=${shareText}%0A%0A${encodeURIComponent(shareUrl)}`}
                data-testid="share-email"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-white text-slate-900 border border-slate-200 hover:border-slate-300 font-medium tracking-tight transition-all"
              >
                <Mail className="w-4 h-4" />
                {t("landing.share.email")}
              </a>
              <button
                onClick={onCopy}
                data-testid="share-copy"
                className="inline-flex items-center justify-center gap-3 px-6 py-4 rounded-full bg-white text-slate-700 border border-slate-200 hover:border-slate-300 font-medium tracking-tight transition-all text-sm"
              >
                <Copy className="w-4 h-4" />
                {t("landing.share.copy")}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="relative max-w-7xl mx-auto px-6 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">{t("landing.faq.eyebrow")}</div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight leading-tight">
              {t("landing.faq.title_1")}<br /><span className="ar-gradient-text">{t("landing.faq.title_2")}</span>
            </h2>
            <p className="text-sm text-slate-500 mt-6 max-w-sm leading-relaxed">
              {t("landing.faq.sub")}
            </p>
          </div>
          <div className="lg:col-span-8 space-y-3">
            {faqItems.map((item, i) => (
              <details
                key={i}
                data-testid={`faq-item-${i}`}
                className="group bg-white border border-slate-200 rounded-2xl p-6 transition-all hover:border-slate-300"
              >
                <summary className="cursor-pointer flex items-start justify-between gap-6 list-none">
                  <h3 className="text-base font-semibold text-slate-900 tracking-tight leading-snug">
                    {item.q}
                  </h3>
                  <span className="shrink-0 mt-1 w-6 h-6 rounded-full bg-slate-100 group-hover:bg-fuchsia-100 flex items-center justify-center transition">
                    <span className="text-slate-700 group-open:rotate-45 transition-transform text-lg leading-none">+</span>
                  </span>
                </summary>
                <p className="text-sm text-slate-600 mt-4 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* METHODE */}
      <section id="methode" className="relative bg-slate-950 text-white overflow-hidden">
        <div className="ar-grid-circuits absolute inset-0 opacity-30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="text-[11px] tracking-[0.18em] uppercase text-slate-400 font-semibold mb-4">{t("landing.method.eyebrow")}</div>
              <h2 className="text-3xl lg:text-5xl font-bold tracking-tight leading-tight">
                {t("landing.method.title_1")} <span className="ar-gradient-text">{t("landing.method.title_2")}</span>
              </h2>
            </div>
            <div className="lg:col-span-7 space-y-8">
              {methodItems.map((b, i) => (
                <div key={i} className="flex gap-6">
                  <div className="text-3xl font-bold ar-gradient-text shrink-0 w-14">{String(i + 1).padStart(2, "0")}</div>
                  <div>
                    <div className="text-lg font-semibold mb-1.5 tracking-tight">{b.t}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24">
        <div className="grid md:grid-cols-2 gap-12 items-center">
          <div>
            <div className="text-[11px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-4">{t("landing.cta_final.eyebrow")}</div>
            <h2 className="text-3xl lg:text-5xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t("landing.cta_final.title_1")} <br />{t("landing.cta_final.title_2")} <span className="ar-gradient-text">{t("landing.cta_final.title_3")}</span>
            </h2>
            <p className="text-slate-600 leading-relaxed max-w-lg">
              {t("landing.cta_final.para")}
            </p>
            <button
              onClick={() => nav("/calculer")}
              data-testid="bottom-cta-start"
              className="group mt-8 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-slate-900 text-white font-medium tracking-tight transition-all hover:bg-slate-950 ar-glow"
            >
              {t("landing.cta_final.cta")}
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {chips.map((x, i) => (
              <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6">
                <x.i className={`w-5 h-5 ${x.c} mb-4`} />
                <div className="text-sm font-semibold text-slate-900">{x.t}</div>
                <div className="text-xs text-slate-500 mt-1">{x.d}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function TrustCard({ icon: Icon, eyebrow, title, text, testid }) {
  return (
    <div data-testid={testid} className="group bg-white rounded-2xl p-6 border border-slate-200 hover:border-slate-300 transition-all">
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-xl bg-slate-900 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-white" />
        </div>
        <div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-slate-500 font-semibold mb-1">{eyebrow}</div>
          <div className="text-base font-semibold text-slate-900 tracking-tight leading-snug">{title}</div>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">{text}</p>
        </div>
      </div>
    </div>
  );
}
